import { contrastRatio, relativeLuminance } from "@/lib/tool-kit/charts/color";
import { normalizeDoi } from "@/lib/tool-kit/identifiers/identifier-input";
import { isAlphanumeric } from "@/lib/tool-kit/qr/qrcodegen";

/**
 * What goes into a QR code, and what comes out of one: the payload for a text, a web
 * address or a DOI; the SVG path of the modules; and a check on the colours.
 */

export type QrContentKind = "text" | "url" | "doi";

export const DOI_RESOLVER = "https://doi.org/";

/**
 * Characters a DOI may contain that a URL may not carry as they are. The DOI
 * Handbook's URL encoding rules make the first five mandatory (% " # space ?) and
 * recommend the rest.
 */
const DOI_URL_ESCAPES: Readonly<Record<string, string>> = {
  "%": "%25",
  '"': "%22",
  "#": "%23",
  " ": "%20",
  "?": "%3F",
  "<": "%3C",
  ">": "%3E",
  "{": "%7B",
  "}": "%7D",
  "^": "%5E",
  "[": "%5B",
  "]": "%5D",
  "`": "%60",
  "|": "%7C",
  "\\": "%5C",
  "+": "%2B",
};

/** A DOI as a doi.org link: 10.1038/171737a0 → https://doi.org/10.1038/171737a0. */
export function doiUrl(doi: string): string {
  let path = "";
  for (const char of doi) {
    path += DOI_URL_ESCAPES[char] ?? char;
  }
  return `${DOI_RESOLVER}${path}`;
}

/**
 * Can the link go in capitals? DOI names and the doi.org address are case-insensitive,
 * and a link in capitals fits QR's 45-character alphanumeric mode (5.5 bits a
 * character instead of 8) — but only if every character is in that set.
 */
export function capitalsFit(url: string): boolean {
  return isAlphanumeric(url.toUpperCase());
}

export type QrPayload =
  | { ok: true; text: string; doi: string | null }
  | { ok: false; problem: "empty" | "badDoi" };

const URL_SCHEME = /^[a-z][a-z0-9+.-]*:/i;

/** Does a web address start with a scheme a phone will open (https:, mailto:…)? */
export function hasScheme(url: string): boolean {
  return URL_SCHEME.test(url.trim());
}

export function qrPayload(
  kind: QrContentKind,
  raw: string,
  capitals: boolean
): QrPayload {
  if (kind === "text") {
    return raw === ""
      ? { ok: false, problem: "empty" }
      : { ok: true, text: raw, doi: null };
  }
  const trimmed = raw.trim();
  if (trimmed === "") {
    return { ok: false, problem: "empty" };
  }
  if (kind === "url") {
    return { ok: true, text: trimmed, doi: null };
  }
  const doi = normalizeDoi(trimmed);
  if (doi === null) {
    return { ok: false, problem: "badDoi" };
  }
  const url = doiUrl(doi);
  return {
    ok: true,
    text: capitals && capitalsFit(url) ? url.toUpperCase() : url,
    doi,
  };
}

/**
 * The dark modules as one SVG path, a run of each row per subpath, offset by the quiet
 * zone. One path rather than a rectangle per module keeps a version-40 code small.
 */
export function qrPath(
  modules: readonly (readonly boolean[])[],
  margin: number
): string {
  const parts: string[] = [];
  for (const [y, row] of modules.entries()) {
    let x = 0;
    while (x < row.length) {
      if (!row[x]) {
        x += 1;
        continue;
      }
      let end = x;
      while (end < row.length && row[end]) {
        end += 1;
      }
      parts.push(`M${x + margin} ${y + margin}h${end - x}v1h-${end - x}z`);
      x = end;
    }
  }
  return parts.join("");
}

/** The quiet zone ISO/IEC 18004 asks for around a QR code, in modules. */
export const STANDARD_QUIET_ZONE = 4;

/** Below this WCAG contrast ratio, warn that phones may struggle. A rule of thumb. */
export const MIN_QR_CONTRAST = 4;

export interface ColorCheck {
  ratio: number;
  /** The modules are lighter than the background. */
  inverted: boolean;
  low: boolean;
}

export function checkColors(dark: string, light: string): ColorCheck {
  const ratio = contrastRatio(dark, light);
  return {
    ratio,
    inverted: relativeLuminance(dark) > relativeLuminance(light),
    low: ratio < MIN_QR_CONTRAST,
  };
}
